// The server keeps one character at the live end of the conversation, with its
// status bubble beside it. When that spot moves ON SCREEN (a new turn below a
// list that stayed put, the empty welcome giving way to the conversation), the
// character and its bubble glide there together, without reparenting server
// DOM or changing scroll intent.
//
// Moving with the list is not moving. Every scroll re-measures where the
// character is, so a reply growing under a list that follows it (AutoScroll
// scrolls before our next frame) leaves the character and bubble still. This
// used to animate the character alone by how far the reply grew, so on every
// growth step it jumped up away from its bubble and eased back down.
export default {
  mounted () {
    this.scroller = this.el.closest('#messages-scroll-container')
    this.anchor = this.el.querySelector('[data-companion-anchor]')
    this.character = this.anchor.firstElementChild
    this.body = this.el.querySelector('[data-companion-body]')
    this.setupTooltip()
    this.chatId = this.el.dataset.chatId
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    this.previous = this.position()
    this.onScroll = () => { this.previous = this.position() }
    this.scroller.addEventListener('scroll', this.onScroll, { passive: true })
    this.onMotionChange = () => {
      if (this.motionQuery.matches || document.hidden) { this.cancel() }
    }
    this.motionQuery.addEventListener('change', this.onMotionChange)
    document.addEventListener('visibilitychange', this.onMotionChange)

    this.resizeObserver = new ResizeObserver(() => this.schedule())
    this.resizeObserver.observe(this.el)
    this.observeContent()
    this.observer = new MutationObserver((mutations) => {
      // Ignore the character's SVG and our status text. LiveView's updated
      // callback covers this component; only sibling content can move it.
      if (mutations.some((mutation) => !this.el.contains(mutation.target))) {
        this.observeContent()
        this.schedule()
      }
    })
    this.observer.observe(this.scroller, { childList: true, subtree: true, characterData: true })
  },

  updated () {
    this.observeContent()
    this.schedule()
  },

  destroyed () {
    cancelAnimationFrame(this.frame)
    this.cancel()
    this.scroller.removeEventListener('scroll', this.onScroll)
    this.observer.disconnect()
    this.resizeObserver.disconnect()
    this.motionQuery.removeEventListener('change', this.onMotionChange)
    document.removeEventListener('visibilitychange', this.onMotionChange)
    this.tooltipListeners.abort()
  },

  setupTooltip () {
    this.tooltipListeners = new AbortController()
    const options = { signal: this.tooltipListeners.signal }
    const show = () => { delete this.anchor.dataset.tipDismissed }
    this.anchor.addEventListener('pointerenter', show, options)
    this.anchor.addEventListener('focusin', show, options)
    this.anchor.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.anchor.dataset.tipDismissed = 'true'
        event.stopPropagation()
      }
    }, options)
    this.character.addEventListener('click', () => {
      this.anchor.dataset.tipDismissed = 'true'
      document.getElementById('message-input')?.focus({ preventScroll: true })
    }, options)
  },

  observeContent () {
    const content = this.scroller.querySelector('#conversation-content')
    if (content === this.content) { return }
    if (this.content) { this.resizeObserver.unobserve(this.content) }
    this.content = content
    if (content) { this.resizeObserver.observe(content) }
  },

  schedule () {
    if (this.frame !== undefined) { return }
    this.frame = requestAnimationFrame(() => {
      this.frame = undefined
      this.move()
    })
  },

  // Where the character's spot is on screen, without the glide in flight.
  position () {
    const anchor = this.anchor.getBoundingClientRect()
    const scroller = this.scroller.getBoundingClientRect()
    const { m41, m42 } = this.pose()
    return { x: anchor.left - scroller.left - m41, y: anchor.top - scroller.top - m42 }
  },

  pose (part = this.anchor) {
    return new DOMMatrixReadOnly(getComputedStyle(part).transform)
  },

  cancel () {
    for (const animation of this.animations ?? []) { animation.cancel() }
    this.animations = []
  },

  move () {
    const next = this.position()
    const previous = this.previous
    this.previous = next
    if (this.chatId !== this.el.dataset.chatId || this.motionQuery.matches || document.hidden) {
      this.chatId = this.el.dataset.chatId
      this.cancel()
      return
    }
    const dx = previous.x - next.x
    const dy = previous.y - next.y
    if (Math.abs(dx) < 1 && Math.abs(dy) < 1) { return }

    // Each part continues from its own current pose if it moved again
    // mid-glide (the bubble sits still through a glide of the character
    // alone). The bubble comes too, except when the layout itself changed
    // sideways (the empty welcome giving way to the conversation), where it
    // is new.
    const parts = Math.abs(dx) < 1 && this.body ? [this.anchor, this.body] : [this.anchor]
    const starts = parts.map((part) => {
      const { m41, m42 } = this.pose(part)
      return `translate(${dx + m41}px, ${dy + m42}px)`
    })
    this.cancel()
    this.animations = parts.map((part, index) => part.animate([
      { transform: starts[index] },
      { transform: 'translate(0, 0)' }
    ], { duration: 240, easing: 'ease-out' }))
  }
}
